import React, { createContext, useCallback, useEffect, useState } from 'react';
import { EditorView } from 'prosemirror-view';
import useThrottledCallback from '@src/hooks/useThrottledCallback';
import { Mark } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';

type TDispatchContextData = {
  applyAlignment: (alignment: string) => void;
  applyColor: (color: string) => void;
  applyFont: (name: string) => void;
  applySize: (size: number) => void;
  focusEditor: () => void;
  setActiveView: (view: EditorView) => void;
  setTextEditorContainerFocused: (containerFocused: boolean) => void;
  toggleBold: (shouldBeBold: boolean) => void;
  toggleItalic: (shouldBeItalic: boolean) => void;
  updateSelectionData: (view: EditorView) => void;
};
type TStateContextData = {
  activeView: EditorView;
  textEditorContainerFocused: boolean;
  isBold: boolean;
  isItalic: boolean;
  selectedAlignment: string;
  selectedColor: string;
  selectedFont: string;
  selectedSize: number;
};

type TProps = {
  children: React.ReactNode;
};

interface ApplyMarkProps {
  view: EditorView;
  mark: Mark;
  focusAfterChange?: boolean;
  applyToAllContent?: boolean;
}

const DEFAULTS = {
  alignment: 'left',
  bold: false,
  color: '#333333',
  font: 'open-sans',
  italic: false,
  size: 15
};

export const TextEditorStateContext = createContext<TStateContextData>({
  isBold: DEFAULTS.bold,
  isItalic: DEFAULTS.italic,
  selectedAlignment: DEFAULTS.alignment,
  selectedColor: DEFAULTS.color,
  selectedFont: DEFAULTS.font,
  selectedSize: DEFAULTS.size
});
export const TextEditorDispatchContext = createContext<TDispatchContextData>(
  {}
);

function applyMark({
  view,
  mark,
  focusAfterChange = true,
  applyToAllContent = false
}: ApplyMarkProps) {
  let state = view.state;
  const transaction = state.tr;
  const { from, to } = state.selection;
  const markName = mark.type.name;
  const markValue = mark.attrs[markName];

  state.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (node.type.name === 'paragraph' && node.content.size === 0) {
      transaction.setNodeMarkup(pos, null, {
        ...node.attrs,
        marks: { ...node.attrs.marks, [markName]: markValue }
      });
    }
  });

  if (applyToAllContent) {
    transaction.addMark(1, state.doc.content.size - 1, mark);
  } else if (from === to) {
    transaction.addStoredMark(mark);
  } else {
    transaction.addMark(from, to, mark);
  }

  state = state.apply(transaction);
  view.updateState(state);

  if (focusAfterChange) view.focus();
}

function newMark(state: EditorState, name: string, value: any): Mark {
  return state.schema.marks[name].create({
    [name]: value
  });
}

interface ApplyBlockAttrProps {
  view: EditorView;
  attrName: string;
  value: string;
  applyToAllContent: boolean;
}

function applyBlockAttr({
  view,
  attrName,
  value,
  applyToAllContent
}: ApplyBlockAttrProps) {
  let state = view.state;
  const transform = state.tr;

  let from: number;
  let to: number;

  if (applyToAllContent) {
    from = 1;
    to = state.doc.content.size - 1;
  } else {
    from = state.selection.from;
    to = state.selection.from;
  }

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'paragraph') {
      transform.setNodeMarkup(
        pos,
        null,
        { ...node.attrs, [attrName]: value },
        null
      );
    }
  });

  state = state.apply(transform);
  view.updateState(state);
  if (!applyToAllContent) view.focus();
}

export function TextEditorProvider({ children }: TProps): React.ReactElement {
  const [activeView, setActiveView] = useState<EditorView>(null);
  const [selectedAlignment, setSelectedAlignment] = useState(
    DEFAULTS.alignment
  );
  const [selectedColor, setSelectedColor] = useState(DEFAULTS.color);
  const [selectedFont, setSelectedFont] = useState(DEFAULTS.font);
  const [selectedSize, setSelectedSize] = useState(DEFAULTS.size);
  const [isBold, setIsBold] = useState(DEFAULTS.bold);
  const [isItalic, setIsItalic] = useState(DEFAULTS.italic);
  const [textEditorContainerFocused, setTextEditorContainerFocused] = useState(
    false
  );

  const updateSelectionData = useThrottledCallback(
    (view: EditorView) => {
      const state = view.state;
      const selection = state.selection;
      const marks = state.doc.resolve(selection.to).marks();
      const storedMarks = state.storedMarks;
      let paragraph;

      state.doc.nodesBetween(
        state.selection.from,
        state.selection.from,
        (node, pos) => {
          if (node.type.name === 'paragraph') {
            paragraph = node;
          }
        }
      );

      setSelectedAlignment(
        paragraph?.attrs['text-align'] || DEFAULTS.alignment
      );

      marks
        .concat(storedMarks)
        .filter((m) => m)
        .forEach((mark: Mark) => {
          const json = mark.toJSON();

          switch (json.type) {
            case 'color':
              setSelectedColor(json.attrs['color']);
              break;
            case 'font-family':
              setSelectedFont(json.attrs['font-family']);
              break;
            case 'font-size':
              setSelectedSize(json.attrs['font-size']);
              break;
            case 'font-style':
              setIsItalic(json.attrs['font-style'] === 'italic');
              break;
            case 'font-weight':
              const weight = json.attrs['font-weight'];
              setIsBold(weight === '700' || weight === 'bold');
              break;
          }
        });
    },
    [],
    100
  );

  useEffect(() => {
    if (activeView) updateSelectionData(activeView);
  }, [activeView, updateSelectionData]);

  const applyAlignment = useCallback(
    (alignment: string) => {
      if (!activeView) return;

      applyBlockAttr({
        view: activeView,
        attrName: 'text-align',
        value: alignment,
        applyToAllContent: textEditorContainerFocused
      });

      setSelectedAlignment(alignment);
    },
    [activeView, textEditorContainerFocused]
  );

  const applyColor = useCallback(
    (color: string) => {
      if (!activeView) return;

      applyMark({
        view: activeView,
        mark: newMark(activeView.state, 'color', color),
        focusAfterChange: false,
        applyToAllContent: textEditorContainerFocused
      });

      setSelectedColor(color);
    },
    [activeView, textEditorContainerFocused]
  );

  const applyFont = useCallback(
    (name: string) => {
      if (!activeView) return;

      applyMark({
        view: activeView,
        mark: newMark(activeView.state, 'font-family', name),
        focusAfterChange: !textEditorContainerFocused,
        applyToAllContent: textEditorContainerFocused
      });

      if (!isBold) {
        let weightMark;

        if (name === 'open-sans') {
          weightMark = newMark(activeView.state, 'font-weight', '300');
        } else {
          weightMark = newMark(activeView.state, 'font-weight', 'normal');
        }

        applyMark({
          view: activeView,
          mark: weightMark,
          focusAfterChange: !textEditorContainerFocused,
          applyToAllContent: textEditorContainerFocused
        });
      }

      setSelectedFont(name);
    },
    [activeView, textEditorContainerFocused, isBold]
  );

  const applySize = useCallback(
    (size: number) => {
      if (!activeView) return;

      applyMark({
        view: activeView,
        mark: newMark(activeView.state, 'font-size', size),
        focusAfterChange: !textEditorContainerFocused,
        applyToAllContent: textEditorContainerFocused
      });

      setSelectedSize(size);
    },
    [activeView, textEditorContainerFocused]
  );

  const focusEditor = useCallback(() => {
    activeView?.focus();
  }, [activeView]);

  const toggleBold = useCallback(
    (shouldBeBold: boolean) => {
      if (!activeView) return;

      applyMark({
        view: activeView,
        mark: newMark(
          activeView.state,
          'font-weight',
          shouldBeBold
            ? 'bold'
            : selectedFont === 'open-sans'
            ? '300'
            : 'normal'
        ),
        focusAfterChange: !textEditorContainerFocused,
        applyToAllContent: textEditorContainerFocused
      });

      setIsBold(shouldBeBold);
    },
    [activeView, selectedFont, textEditorContainerFocused]
  );

  const toggleItalic = useCallback(
    (shouldBeItalic: boolean) => {
      if (!activeView) return;

      applyMark({
        view: activeView,
        mark: newMark(
          activeView.state,
          'font-style',
          shouldBeItalic ? 'italic' : 'normal'
        ),
        focusAfterChange: !textEditorContainerFocused,
        applyToAllContent: textEditorContainerFocused
      });

      setIsItalic(shouldBeItalic);
    },
    [activeView, textEditorContainerFocused]
  );

  return (
    <TextEditorDispatchContext.Provider
      value={{
        applyAlignment,
        applyColor,
        applyFont,
        applySize,
        focusEditor,
        setActiveView,
        setTextEditorContainerFocused,
        toggleBold,
        toggleItalic,
        updateSelectionData
      }}
    >
      <TextEditorStateContext.Provider
        value={{
          activeView: activeView,
          textEditorContainerFocused,
          isBold,
          isItalic,
          selectedAlignment,
          selectedColor,
          selectedFont,
          selectedSize
        }}
      >
        {children}
      </TextEditorStateContext.Provider>
    </TextEditorDispatchContext.Provider>
  );
}
